import { URLS } from '../config/app-config';
/**
 * TOC helpers
 * ------------------------------------------
 *  flattenToc        – returns a depth‑first flat array of the tree
 *  normalizeToc      – fixes MongoDB { $oid } IDs recursively
 *  loadOfflineToc    – fetches the offline JSON TOC bundle
 *  loadOnlineToc     – fetches the online TOC from the API
 */

import { trySecureFetchWithHooks } from "../utils/secureFetch";

export const flattenToc = (toc) => {
    let result = [];
    toc.forEach(item => {
        result.push(item);
        if (item.children?.length) result = result.concat(flattenToc(item.children));
    });
    return result;
};

// Normalise Mongo‑exported IDs ({ $oid: '...' }) recursively
export const normalizeToc = (nodes) =>
    nodes.map(node => {
        const id = node._id?.$oid || node._id;
        const contentRef = node.contentRef?.$oid || node.contentRef;
        const children = node.children ? normalizeToc(node.children) : [];
        return { ...node, _id: id, contentRef, children };
    });

const BASE_URL = URLS.apiBase.replace(/\/$/, '');

/**
 * Common post‑fetch steps (used by both loaders)
 */
const afterFetch = ({
                        toc,
                        pickInitial,
                        handleArticleSelect,
                        setTocData,
                        setFlatToc,
                        previousReleaseRef,
                        selectedRelease,
                    }) => {
    setTocData(toc);
    const flat = flattenToc(toc);
    setFlatToc(flat);

    const initial = pickInitial(flat, previousReleaseRef.current);
    if (initial) handleArticleSelect(initial, flat);

    previousReleaseRef.current = selectedRelease;
};

/**
 * Offline JSON file
 * @param {object} options - Fetch options, including AbortSignal
 * @param {Release} options.selectedRelease - Documentation release
 * @param {function} options.pickInitial - Function to pick the initial article
 * @param {function} options.handleArticleSelect - Function to handle article selection
 * @param {function} options.setTocData - Function to set the TOC data
 * @param {function} options.setFlatToc - Function to set the flattened TOC data
 * @param {function} options.inc - Function to increment the loading counter
 * @param {function} options.dec - Function to decrement the loading counter
 * @param {function} options.setError - Function to set the error message
 * @param {React.MutableRefObject<Release>} options.previousReleaseRef - Reference to the previous release
 * @param {AbortSignal} options.signal - Optional AbortSignal to cancel the request
 */
export const loadOfflineToc = async ({
                                         selectedRelease,
                                         pickInitial,
                                         handleArticleSelect,
                                         setTocData,
                                         setFlatToc,
                                         inc,
                                         dec,
                                         setError,
                                         previousReleaseRef,
                                         signal,
                                     }) => {

    const toc = await trySecureFetchWithHooks(
        `/data/${selectedRelease.version}_${selectedRelease.language}/help_${selectedRelease.version}_${selectedRelease.language}_toc.json`,
        { signal },
        false,
        true,
        {
            onBeforeFetch: () => inc(),
            onAfterFetch: () => dec(),
            onError: (err) => {
                console.error('Offline TOC fetch error:', err);
                setError(err.message);
            }
        }
    ).then(r => {
        if (r?.ok) return r?.json();
        return null;
    });

    if (toc === null) return;
    afterFetch({
        toc,
        pickInitial,
        handleArticleSelect,
        setTocData,
        setFlatToc,
        previousReleaseRef,
        selectedRelease,
    });
};

/**
 * Online API
 * @param {object} options - Fetch options, including AbortSignal
 * @param {Release} options.selectedRelease - Documentation release
 * @param {function} options.pickInitial - Function to pick the initial article
 * @param {function} options.handleArticleSelect - Function to handle article selection
 * @param {function} options.setTocData - Function to set the TOC data
 * @param {function} options.setFlatToc - Function to set the flattened TOC data
 * @param {function} options.inc - Function to increment the loading counter
 * @param {function} options.dec - Function to decrement the loading counter
 * @param {function} options.setError - Function to set the error message
 * @param {React.MutableRefObject<Release>} options.previousReleaseRef - Reference to the previous release
 * @param {AbortSignal} options.signal - Optional AbortSignal to cancel the request
 */
export const loadOnlineToc = async ({
                                        selectedRelease,
                                        pickInitial,
                                        handleArticleSelect,
                                        setTocData,
                                        setFlatToc,
                                        inc,
                                        dec,
                                        setError,
                                        previousReleaseRef,
                                        signal,
                                    }) => {

    const toc = await trySecureFetchWithHooks(
        `${BASE_URL}/toc?${selectedRelease.toSearchParams()}`,
        { signal },
        false,
        true,
        {
            onBeforeFetch: () => inc(),
            onAfterFetch: () => dec(),
            onError: (err) => {
                console.error('Online TOC fetch error:', err);
                setError(err.message);
            }
        }
    ).then(r => {
        if (r?.ok) return r?.json();
        return null;
    });

    if (toc === null) return;
    afterFetch({
        toc,
        pickInitial,
        handleArticleSelect,
        setTocData,
        setFlatToc,
        previousReleaseRef,
        selectedRelease,
    });
};

